import type { ReactNode } from 'react';
import { Card, MicroLabel, SectionHead } from './ui/primitives';
import type { BrandStat } from './types';

/**
 * The brand in one screen: its description, and a strip counting what the page
 * below holds.
 *
 * The strip's job is to answer "is this brand kit actually filled in" before
 * anyone scrolls — a brand with 0 logos and 2 colors is a brand someone needs
 * to chase, and that used to take scrolling past four empty sections to find
 * out. Each figure is a link into the section that owns it, so the count is
 * also the way to get there.
 */
export function BrandOverview({
	description,
	stats,
	onStatClick,
	action,
	footer,
}: {
	description?: string | null;
	stats: BrandStat[];
	/** Passed the stat's `sectionId`, when it has one. */
	onStatClick?: (sectionId: string) => void;
	action?: ReactNode;
	/** Anything the app wants under the strip — an access summary, a link out. */
	footer?: ReactNode;
}) {
	return (
		<>
			<SectionHead title="Overview" action={action} />
			<Card padded>
				{description ? (
					<p className="mb-5 max-w-[40rem] text-base leading-relaxed text-text-1">
						{description}
					</p>
				) : (
					<p className="mb-5 text-sm text-text-4">
						No description yet — a line about what this brand is helps everyone working
						on it.
					</p>
				)}

				<div className="grid grid-cols-2 overflow-hidden rounded-[var(--r-2)] border border-line-1 bg-surface-sunken sm:grid-cols-4">
					{stats.map((stat, i) => (
						<StatCell
							key={stat.label}
							stat={stat}
							/* Borders on the cell rather than a gap: the strip is
							   one ruled block, and a gap would let the page
							   background through between figures. */
							className={i > 0 ? 'border-l border-line-1' : undefined}
							onClick={onStatClick}
						/>
					))}
				</div>

				{footer && <div className="mt-4">{footer}</div>}
			</Card>
		</>
	);
}

function StatCell({
	stat,
	className,
	onClick,
}: {
	stat: BrandStat;
	className?: string;
	onClick?: (sectionId: string) => void;
}) {
	const target = stat.sectionId;
	const interactive = Boolean(target && onClick);

	return (
		<button
			type="button"
			disabled={!interactive}
			onClick={interactive && target ? () => onClick?.(target) : undefined}
			className={`px-4 py-3 text-left transition-colors duration-[var(--dur-fast)] ${
				interactive ? 'hover:bg-surface-hover' : 'cursor-default'
			} ${className ?? ''}`}
		>
			<MicroLabel className="mb-1.5 block">{stat.label}</MicroLabel>
			<div className="font-mono text-lg font-medium text-text-1">{stat.value}</div>
		</button>
	);
}
